import { Step, CallBackProps, STATUS } from 'react-joyride';
import { tourTarget } from './dashboardTourTargets';

/** Tour steps. Chart-tile copy (details pane) vs classic (edit modal + comments). */
export const getDashboardTourSteps = (useChartTiles: boolean): Step[] => [
  {
    target: 'body',
    content: (
      <div className="text-center">
        <div className="text-xl font-semibold mb-4">Welcome to Your Dashboard!</div>
        <p className="leading-relaxed">
          Great job creating your first dashboard! Now let's explore how to add metrics, track assurance levels, and
          make the most of this powerful tool.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    styles: {
      options: {
        width: '500px'
      }
    }
  },
  {
    target: '[data-slot="breadcrumb"]',
    content:
      'This breadcrumb shows where you are in your dashboard hierarchy. Click on any level to navigate back up the tree.',
    placement: 'bottom'
  },
  {
    target: tourTarget('periodSelector'),
    content:
      'Choose a week, month, or quarter to view historical data and track how your assurance levels have evolved over time.',
    placement: 'bottom'
  },
  {
    target: tourTarget('trendToggle'),
    content:
      'Switch between the last 3 and last 6 periods. Each card uses its own cadence — weeks, months, or quarters.',
    placement: 'bottom'
  },
  {
    target: tourTarget('assuranceLegend'),
    content:
      'Open the colour key to see the assurance levels used throughout your dashboard — from High (excellent) to None (needs attention).',
    placement: 'bottom'
  },
  {
    target: tourTarget('complimindInsight'),
    content: useChartTiles
      ? 'CompliMind Insight analyses trends on this dashboard and highlights areas that need attention. Use Show more to read the full summary.'
      : 'Click here to get AI-powered insights about your dashboard. CompliMind will analyse trends and highlight areas that need attention.',
    placement: 'bottom'
  },
  {
    target: tourTarget('heroTile'),
    content: (
      <div>
        <div className="text-lg font-semibold mb-3">Overall Dashboard Assurance</div>
        <p className="leading-relaxed mb-3">
          This is your overall dashboard assurance level. It aggregates all child metrics to give you a quick health
          check.
        </p>
        <p className="leading-relaxed text-sm mb-2">
          {useChartTiles ? (
            <>
              <strong>Look for the details icon</strong> in the top-right corner of any card to open the details pane —
              this period&apos;s value, comments, history, and card settings.
            </>
          ) : (
            <>
              <strong>Look for the edit icon (✏️)</strong> in the top-right corner of any card to customise settings
              like the name and aggregation method.
            </>
          )}
        </p>
        <p className="leading-relaxed text-xs text-gray-600">
          {useChartTiles
            ? '💡 Once you have cards, you can also duplicate them or move them between groups from card settings.'
            : '💡 Once you have cards, you can also duplicate them or move them between groups from the edit panel.'}
        </p>
      </div>
    ),
    placement: 'bottom',
    styles: {
      options: {
        width: '480px'
      }
    }
  },
  {
    target: tourTarget('metricsGrid'),
    content:
      'Add metrics here to track specific indicators. You can create Group Cards to organise metrics hierarchically, or Data Cards to track individual measurements.',
    placement: 'top'
  },
  {
    target: tourTarget('addMetricCard'),
    content: (
      <div>
        <div className="text-xl font-semibold mb-4">Start Adding Metrics</div>
        <p className="leading-relaxed mb-3">Click here to add your first metric! You can create:</p>
        <ul className="list-disc list-inside space-y-2 text-sm">
          <li>
            <strong>Group Cards:</strong> Organise multiple metrics into categories (e.g., "Fire Safety", "Water
            Quality")
          </li>
          <li>
            <strong>Data Cards:</strong> Track specific measurements or KPIs with values and assurance levels
          </li>
        </ul>
        <p className="leading-relaxed text-sm text-gray-600 mt-3">
          Not sure where to start? Use the chat examples under the empty state and chat will propose cards for this
          dashboard — you can upload a spreadsheet there too.
        </p>
      </div>
    ),
    placement: 'left',
    styles: {
      options: {
        width: '450px'
      }
    }
  },
  {
    target: 'body',
    content: (
      <div className="text-center">
        <div className="text-xl font-semibold mb-4">You're ready to build!</div>
        <p className="leading-relaxed mb-3">
          Start adding metrics to your dashboard. Each metric can have data values, trend analysis, and automatic
          assurance level calculations.
        </p>
        <p className="leading-relaxed text-sm text-gray-600 mt-4">
          As you build, you'll discover more features like duplicating cards, moving them between groups, and linking
          cards across dashboards.
        </p>
      </div>
    ),
    placement: 'center',
    styles: {
      options: {
        width: '500px'
      }
    }
  }
];

// Tour styles configuration
export const dashboardTourStyles = {
  options: {
    primaryColor: '#222B6E',
    zIndex: 1000
  }
};

// Tour locale configuration
export const dashboardTourLocale = {
  back: 'Previous',
  close: 'Close',
  last: 'Finish',
  next: 'Next',
  skip: 'Skip tour'
};

// Tour callback handler
export const handleDashboardTourCallback = (data: CallBackProps, setRunTour: (run: boolean) => void) => {
  const { status } = data;
  if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
    setRunTour(false);
    localStorage.setItem('dashboard-tour-completed', 'true');
    // Remove the flag that triggers auto-start
    localStorage.removeItem('start-dashboard-tour');
  }
};

// Check if user should see the tour automatically
export const shouldShowDashboardTour = (): boolean => {
  const shouldAutoStart = localStorage.getItem('start-dashboard-tour') === 'true';
  const hasSeenTour = localStorage.getItem('dashboard-tour-completed');
  return shouldAutoStart && !hasSeenTour;
};
